import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { deepCamelCaseKeys, denormalizeCampaign, renormalizeCampaign, tokenizePath } from './brief-mapping.util';
import { IBriefAnalysis, IBriefAnalyzeRequest, IBriefValidationMessage } from './brief.interface';

function toBriefAnalysis(raw: Record<string, unknown>): IBriefAnalysis {
  const camelCased = deepCamelCaseKeys(raw) as Record<string, unknown>;

  return {
    campaign: denormalizeCampaign((camelCased['campaign'] as Record<string, unknown>) ?? {}),
    canCreate: !!camelCased['canCreate'],
    costUsd: (camelCased['costUsd'] as number) ?? 0,
    errors: toValidationMessages(camelCased['errors']),
    model: (camelCased['model'] as string) ?? '',
    sourceFilenames: (camelCased['sourceFilenames'] as string[]) ?? [],
    warnings: toValidationMessages(camelCased['warnings']),
  };
}

function toValidationMessages(rawMessages: unknown): IBriefValidationMessage[] {
  if (!Array.isArray(rawMessages)) {
    return [];
  }

  return (rawMessages as Record<string, unknown>[]).map((rawMessage) => {
    // layer/code/message/params are already valid identifiers/plain strings - only `path` still
    // holds the original snake_case API path, kept as-is alongside its tokenized form so a
    // component can show the raw path in a tooltip/debug context if it ever needs to.
    const path = rawMessage['path'] as string;

    return {
      code: rawMessage['code'] as string,
      layer: rawMessage['layer'] as IBriefValidationMessage['layer'],
      message: rawMessage['message'] as string,
      params: (rawMessage['params'] as Record<string, unknown>) ?? {},
      path,
      pathTokens: tokenizePath(path),
    };
  });
}

@Injectable({
  providedIn: 'root',
})
export class BriefService {
  constructor(private readonly http: HttpClient) {}

  public analyzeBrief({ files, prompt }: IBriefAnalyzeRequest): Observable<IBriefAnalysis> {
    const formData = new FormData();
    const trimmedPrompt = prompt?.trim();

    files.forEach((file) => formData.append('files', file));

    if (trimmedPrompt) {
      formData.append('prompt', trimmedPrompt);
    }

    return this.http.post<Record<string, unknown>>('brief/analyze', formData).pipe(map(toBriefAnalysis));
  }

  public validateBrief(campaign: Record<string, unknown>, downgrade: boolean): Observable<IBriefAnalysis> {
    const params = new HttpParams().set('downgrade', downgrade);

    return this.http
      .post<Record<string, unknown>>('brief/validate', renormalizeCampaign(campaign), { params })
      .pipe(map(toBriefAnalysis));
  }
}
